import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { v4 as uuid } from "uuid";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeftIcon, ArrowUpRightIcon, BookmarkSquareIcon, CheckIcon, ChevronRightIcon, LockClosedIcon, MagnifyingGlassIcon, PencilSquareIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import Header from "../components/Header";
import { useAuthContext } from "../contexts/AuthContext";
import { Note, NotePreview, NotesError, useNotes } from "./NotesContext";
import "./Notes.css";

function NotesLibrary() {
  const notes = useNotes()!;
  const request = notes.request;
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState<NotePreview[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refresh, setRefresh] = useState(0);
  const generation = useRef({ value: 0 }).current;
  const controller = useRef<AbortController | undefined>(undefined);
  const load = useCallback(async (offset = 0) => {
    const token = ++generation.value;
    controller.current?.abort(); controller.current = new AbortController();
    setLoading(true); setError("");
    try {
      const data = await request<{ notes: NotePreview[]; has_more: boolean }>(`?q=${encodeURIComponent(query)}&limit=30&offset=${offset}`, { signal: controller.current.signal });
      if (generation.value !== token) return;
      setRows(prev => offset ? [...prev, ...data.notes.filter(n => !prev.some(p => p.id === n.id))] : data.notes);
      setHasMore(data.has_more);
    } catch (e) { if (generation.value === token && (e as Error).name !== "AbortError") setError((e as Error).message); }
    finally { if (generation.value === token) setLoading(false); }
  }, [request, query, generation]);
  useEffect(() => {
    const timer = setTimeout(() => { void load(); }, 250);
    return () => { clearTimeout(timer); ++generation.value; controller.current?.abort(); };
  }, [load, notes.changed, refresh, generation]);
  useEffect(() => {
    const focus = () => { if (document.visibilityState === "visible") setRefresh(n => n + 1); };
    window.addEventListener("focus", focus); document.addEventListener("visibilitychange", focus);
    return () => { window.removeEventListener("focus", focus); document.removeEventListener("visibilitychange", focus); };
  }, []);
  return <>
    <Link className="notes-back" to="/chat"><ArrowLeftIcon aria-hidden="true" />Back to chat</Link>
    <div className="notes-heading">
      <div><span className="notes-eyebrow"><LockClosedIcon aria-hidden="true" />Your private library</span><h1>Notes</h1><p>Keep the answers. Make them your own.</p></div>
      <button className="notes-primary" onClick={() => navigate("/notes/new")}><PlusIcon aria-hidden="true" />New note</button>
    </div>
    <label className="notes-search"><span className="notes-sr-only">Search notes</span><MagnifyingGlassIcon aria-hidden="true" /><input type="search" value={query} onChange={e => { setQuery(e.target.value); setRows([]); }} placeholder="Search titles and content" dir="auto" /></label>
    <div className="notes-list-heading"><span>{query ? "Search results" : "Your notes"}</span><span>Recently edited</span></div>
    {error && <div className="notes-notice" role="alert"><p>{error}</p><button onClick={() => void load()}>Retry</button></div>}
    {loading && <p className="notes-status" role="status"><span className="notes-spinner" aria-hidden="true" />Loading notes…</p>}
    {!loading && !error && !rows.length && <div className="notes-empty">
      {query ? <MagnifyingGlassIcon aria-hidden="true" /> : <BookmarkSquareIcon aria-hidden="true" />}
      <h2>{query ? "No matching notes." : "A place for what matters."}</h2>
      <p>{query ? "Try another word or phrase." : "No notes yet. Write your first note or save an answer from chat."}</p>
    </div>}
    <ul className="notes-list">{rows.map(note => <li key={note.id}>
      <Link to={`/notes/${note.id}`}>
        <div className="notes-row-content"><h2 dir="auto">{note.title}</h2><p dir="auto">{note.preview}</p><div className="notes-row-meta">{note.source_message_id && <span><BookmarkSquareIcon aria-hidden="true" />Saved from chat</span>}<time dateTime={note.updated_at}>{new Date(note.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</time></div></div>
        <ChevronRightIcon className="notes-row-arrow" aria-hidden="true" />
      </Link>
    </li>)}</ul>
    {hasMore && <button disabled={loading} onClick={() => void load(rows.length)}>Load more</button>}
  </>;
}

function NoteDetail({ id }: { id: string }) {
  const notes = useNotes()!;
  const request = notes.request;
  const navigate = useNavigate();
  const isNew = id === "new";
  const [draftId] = useState(() => uuid());
  const [copyId] = useState(() => uuid());
  const [note, setNote] = useState<Note | null>(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [editing, setEditing] = useState(isNew);
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const [missing, setMissing] = useState(false);
  const alive = useRef(true);
  const busy = useRef(false);
  const persistedId = useRef<string | null>(null);
  const dirty = editing && (title !== (note?.title || "") || content !== (note?.content_markdown || ""));
  const dirtyRef = useRef(dirty); dirtyRef.current = dirty;
  const editingRef = useRef(editing); editingRef.current = editing;
  const readSequence = useRef({ value: 0 }).current;
  const apply = useCallback((value: Note) => { setNote(value); setTitle(value.title); setContent(value.content_markdown); setMissing(false); }, []);
  const load = useCallback(async (force = false) => {
    if (isNew && !force) return;
    const sequence = ++readSequence.value;
    setLoading(true); setError("");
    if (force) setPending(true);
    try {
      const value = await request<Note>(`/${isNew ? persistedId.current : id}`);
      if (alive.current && sequence === readSequence.value && (force || !dirtyRef.current)) { apply(value); setConflict(false); if (force && isNew) navigate(`/notes/${value.id}`, { replace: true }); }
    } catch (e) {
      if (alive.current && sequence === readSequence.value && (e as Error).name !== "AbortError") {
        setError((e as Error).message);
        if (e instanceof NotesError && e.status === 404) setMissing(true);
      }
    } finally { if (alive.current && sequence === readSequence.value) { setLoading(false); if (force) setPending(false); } }
  }, [id, isNew, request, apply, readSequence, navigate]);
  useEffect(() => { alive.current = true; void load(); return () => { alive.current = false; ++readSequence.value; }; }, [load, readSequence]);
  useEffect(() => {
    const foreground = () => { if (document.visibilityState === "visible" && !editingRef.current && !busy.current) void load(); };
    window.addEventListener("focus", foreground); document.addEventListener("visibilitychange", foreground);
    return () => { window.removeEventListener("focus", foreground); document.removeEventListener("visibilitychange", foreground); };
  }, [load]);
  useEffect(() => {
    const unload = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; } };
    // Cover app links, including the shared header, before React Router handles them.
    const click = (e: MouseEvent) => {
      if (dirtyRef.current && (e.target as Element).closest("a[href]") && !window.confirm("Discard unsaved changes?")) { e.preventDefault(); e.stopPropagation(); }
    };
    const originalIndex = window.history.state?.idx ?? 0;
    let restoring = false;
    const pop = (e: PopStateEvent) => {
      if (restoring) { restoring = false; e.stopImmediatePropagation(); return; }
      if (!dirtyRef.current) return;
      if (window.confirm("Discard unsaved changes?")) { dirtyRef.current = false; return; }
      e.stopImmediatePropagation();
      const delta = originalIndex - (window.history.state?.idx ?? originalIndex);
      if (delta) { restoring = true; window.history.go(delta); }
    };
    window.addEventListener("popstate", pop, true);
    window.addEventListener("beforeunload", unload); document.addEventListener("click", click, true);
    return () => { window.removeEventListener("popstate", pop, true); window.removeEventListener("beforeunload", unload); document.removeEventListener("click", click, true); };
  }, []);
  const save = async (asCopy = false) => {
    if (busy.current) return;
    busy.current = true; ++readSequence.value; setPending(true); setError("");
    try {
      const create = (isNew && !note) || asCopy;
      const value = await request<Note>(create ? "" : `/${note?.id || id}`, { method: create ? "POST" : "PATCH", body: JSON.stringify({ title, content_markdown: content, ...(create ? { id: asCopy ? copyId : draftId } : { revision: note!.revision }) }) });
      notes.remember(value);
      if (!alive.current) return;
      if (create && (value.content_markdown !== content || (title.trim() && value.title !== title.trim()))) {
        persistedId.current = value.id;
        setNote(value); setConflict(true);
        setError("A previous save completed with different content. Your current draft has been kept.");
        return;
      }
      apply(value); setEditing(false); setConflict(false); dirtyRef.current = false;
      if (create || isNew) navigate(`/notes/${value.id}`, { replace: true });
    } catch (e) {
      if (alive.current && (e as Error).name !== "AbortError") { setError((e as Error).message); setConflict(e instanceof NotesError && e.status === 409); }
    } finally { busy.current = false; if (alive.current) setPending(false); }
  };
  const cancel = () => {
    if (dirty && !window.confirm("Discard unsaved changes?")) return;
    dirtyRef.current = false;
    if (note) { apply(note); setEditing(false); setConflict(false); setError(""); }
    else navigate("/notes");
  };
  const remove = async () => {
    if (!note || busy.current || !window.confirm("Delete this note permanently?")) return;
    busy.current = true; setPending(true); setError("");
    try { await notes.remove(note); if (alive.current) { dirtyRef.current = false; navigate("/notes"); } }
    catch (e) { if (alive.current) setError((e as Error).message); }
    finally { busy.current = false; if (alive.current) setPending(false); }
  };
  return <>
    <Link className="notes-back" to="/notes"><ArrowLeftIcon aria-hidden="true" />All notes</Link>
    {loading && <p className="notes-status" role="status"><span className="notes-spinner" aria-hidden="true" />Loading note…</p>}
    {error && <div className="notes-notice" role="alert"><p>{error}</p>{!conflict && !missing && !editing && <button onClick={() => void load()}>Retry</button>}</div>}
    {missing && <p>This note is no longer available. Return to chat to save its answer again.</p>}
    {conflict && <div className="notes-actions"><button disabled={pending} onClick={() => { if (window.confirm("Discard your draft and load the latest version?")) { void load(true); } }}>Load latest</button><button disabled={pending} onClick={() => void save(true)}>Save as new note</button></div>}
    {!missing && (isNew || note) && <>
      <div className="notes-heading notes-detail-heading"><div><span className="notes-eyebrow"><LockClosedIcon aria-hidden="true" />{dirty ? "Unsaved changes" : "Only you"}</span><h1 dir="auto">{editing ? isNew ? "New note" : "Edit note" : note?.title}</h1>{!editing && note && <p className="notes-updated">Edited {new Date(note.updated_at).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}</p>}</div><div className="notes-actions">
        {editing ? <><button className="notes-primary" disabled={pending || !content.trim() || title.length > 200} onClick={() => void save()}>{pending ? <span className="notes-spinner" aria-hidden="true" /> : <CheckIcon aria-hidden="true" />}{pending ? "Saving…" : "Save"}</button><button disabled={pending} onClick={cancel}>Cancel</button></> : <><button className="notes-primary" onClick={() => { ++readSequence.value; setLoading(false); setEditing(true); }}><PencilSquareIcon aria-hidden="true" />Edit</button><button className="notes-delete" disabled={pending} onClick={() => void remove()}><TrashIcon aria-hidden="true" />Delete</button></>}
      </div></div>
      {editing && <><label className="notes-field">Title<input className="notes-title-input" disabled={pending} value={title} maxLength={200} onChange={e => setTitle(e.target.value)} dir="auto" placeholder="Optional — generated from your note" /></label><div className="notes-editor-toolbar"><div className="notes-segments" role="group" aria-label="Editor mode"><button aria-pressed={!preview} onClick={() => setPreview(false)}>Edit</button><button aria-pressed={preview} onClick={() => setPreview(true)}>Preview</button></div><span>Markdown supported</span></div></>}
      {editing && !preview ? <label className="notes-field"><span className="notes-sr-only">Note</span><textarea disabled={pending} value={content} onChange={e => setContent(e.target.value)} dir="auto" placeholder="Start with an idea, a question, or a key takeaway…" /></label> : <article className="notes-markdown" dir="auto"><ReactMarkdown remarkPlugins={[remarkGfm]} skipHtml>{content}</ReactMarkdown></article>}
      {note?.source_thread_id && <div className="notes-source"><BookmarkSquareIcon aria-hidden="true" /><span>Saved from chat</span><Link to={`/chat/thread/${note.source_thread_id}`}>Open conversation<ArrowUpRightIcon aria-hidden="true" /></Link></div>}
    </>}
  </>;
}
export default function NotesPage() {
  const { user, loading } = useAuthContext();
  const { noteId } = useParams();
  if (loading) return <p role="status">Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;
  return <div className="notes-workspace"><Header /><main className="notes-page" key={user.id}>{noteId ? <NoteDetail key={noteId} id={noteId} /> : <NotesLibrary />}</main></div>;
}
